import { apiRequest, buildQueryString, Article, PaginationResponse, API_CONFIG, ApiResponse } from '../config/api';

export interface ArticleFilters {
  page?: number;
  limit?: number;
  status?: 'active' | 'archived' | 'hidden';
  featured?: boolean;
  tags?: string[];
  keywords?: string[];
  dateFrom?: string;
  dateTo?: string;
  dailySummaryIds?: number[];
}

export interface ArticleSearchParams {
  term: string;
  page?: number;
  limit?: number;
}

export const articleService = {
  // Get all articles with pagination and filtering
  getArticles: async (filters: ArticleFilters = {}): Promise<PaginationResponse<Article>> => {
    const queryString = buildQueryString(filters);
    return apiRequest<PaginationResponse<Article>>(`${API_CONFIG.ENDPOINTS.ARTICLES}${queryString}`);
  },

  // Get single article by slug
  getArticle: async (slug: string): Promise<Article> => {
    const response = await apiRequest<ApiResponse<Article>>(`${API_CONFIG.ENDPOINTS.ARTICLES}/${slug}`);
    if (!response.data) {
      throw new Error('Article not found');
    }
    return response.data;
  },

  // Search articles
  searchArticles: async (params: ArticleSearchParams): Promise<PaginationResponse<Article>> => {
    const { term, ...filters } = params;
    const queryString = buildQueryString(filters);
    return apiRequest<PaginationResponse<Article>>(
      `${API_CONFIG.ENDPOINTS.ARTICLES}/search/${encodeURIComponent(term)}${queryString}`
    );
  },

  // Get featured articles
  getFeaturedArticles: async (limit: number = 5): Promise<{ articles: Article[] }> => {
    const queryString = buildQueryString({ featured: true, limit });
    const response = await apiRequest<PaginationResponse<Article>>(`${API_CONFIG.ENDPOINTS.ARTICLES}${queryString}`);
    return { articles: response.data };
  },

  // Get recent articles
  getRecentArticles: async (limit: number = 10): Promise<{ articles: Article[] }> => {
    const queryString = buildQueryString({ limit, status: 'active' });
    const response = await apiRequest<PaginationResponse<Article>>(
      `${API_CONFIG.ENDPOINTS.ARTICLES}${queryString}`
    );
    return { articles: response.data };
  },

  // Get articles by tag
  getArticlesByTag: async (tag: string, page: number = 1, limit: number = 10): Promise<PaginationResponse<Article>> => {
    const queryString = buildQueryString({ tags: [tag], page, limit });
    return apiRequest<PaginationResponse<Article>>(`${API_CONFIG.ENDPOINTS.ARTICLES}${queryString}`);
  },

  // Get articles by date range
  getArticlesByDateRange: async (dateFrom: string, dateTo: string, page: number = 1): Promise<PaginationResponse<Article>> => {
    const queryString = buildQueryString({ dateFrom, dateTo, page });
    return apiRequest<PaginationResponse<Article>>(`${API_CONFIG.ENDPOINTS.ARTICLES}${queryString}`);
  },

  // Create new article (admin only)
  createArticle: async (articleData: Partial<Article>): Promise<{ message: string; article: Article }> => {
    return apiRequest<{ message: string; article: Article }>(API_CONFIG.ENDPOINTS.ARTICLES, {
      method: 'POST',
      body: JSON.stringify(articleData),
    });
  },

  // Update article (admin only)
  updateArticle: async (id: number, articleData: Partial<Article>): Promise<{ message: string; article: Article }> => {
    return apiRequest<{ message: string; article: Article }>(`${API_CONFIG.ENDPOINTS.ARTICLES}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(articleData),
    });
  },

  // Delete article (admin only - soft delete)
  deleteArticle: async (id: number): Promise<{ message: string }> => {
    return apiRequest<{ message: string }>(`${API_CONFIG.ENDPOINTS.ARTICLES}/${id}`, {
      method: 'DELETE',
    });
  },

  // Track article view (analytics)
  trackView: async (slug: string): Promise<void> => {
    try {
      // This will be handled by the getArticle endpoint automatically
      // But we can add explicit tracking if needed
      await apiRequest(`${API_CONFIG.ENDPOINTS.ARTICLES}/${slug}/view`, {
        method: 'POST',
      });
    } catch (error) {
      // Don't throw on analytics errors
      console.warn('Failed to track article view:', error);
    }
  },

  // Track article share (analytics)
  trackShare: async (slug: string, platform: string): Promise<void> => {
    try {
      await apiRequest(`${API_CONFIG.ENDPOINTS.ARTICLES}/${slug}/share`, {
        method: 'POST',
        body: JSON.stringify({ platform }),
      });
    } catch (error) {
      console.warn('Failed to track article share:', error);
    }
  },

  // Track external link click (analytics)
  trackClick: async (slug: string): Promise<void> => {
    try {
      await apiRequest(`${API_CONFIG.ENDPOINTS.ARTICLES}/${slug}/click`, {
        method: 'POST',
      });
    } catch (error) {
      console.warn('Failed to track article click:', error);
    }
  },
};
